import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getActivityBank } from '../lib/activityBank.js';
import { exportActivityPdf } from '../lib/exporters.js';
import { canEdit, isProjectAdmin } from '../lib/permissions.js';
import { formatDate, generateId, todayISO } from '../lib/utils.js';
import { activityToLogText } from '../lib/activity-pedagogy.js';
import { ActivityPedagogicalCard } from './ActivityPedagogicalCard.jsx';

function visibleStudents(data, user) {
  if (!user || isProjectAdmin(user)) return [];
  if (user.role === 'school_admin') return (data.students || []).filter((student) => student.schoolId === user.schoolId);
  if (user.role === 'professional') return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.profissionais?.includes(user.uid));
  return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.responsaveis?.includes(user.uid));
}

function schoolOf(data, schoolId) {
  return (data.schools || []).find((school) => school.id === schoolId) || {};
}

function teacherFor(data, user, student) {
  if (user?.role === 'professional' || user?.role === 'school_admin') return user;
  const teacherId = student?.profissionais?.[0];
  return (data.users || []).find((item) => item.uid === teacherId) || {};
}

export function ActivityPage({ user, data, setData, onSave }) {
  const { activityId } = useParams();
  const [searchParams] = useSearchParams();
  const bank = useMemo(() => getActivityBank(data), [data]);
  const students = useMemo(() => visibleStudents(data, user), [data, user]);
  const activity = bank.atividades.find((item) => item.id === activityId);
  const queryStudentId = searchParams.get('studentId') || '';
  const initialStudentId = students.some((student) => student.id === queryStudentId) ? queryStudentId : (students[0]?.id || '');
  const [studentId, setStudentId] = useState(initialStudentId);
  const student = students.find((item) => item.id === studentId);
  const school = schoolOf(data, student?.schoolId || user?.schoolId);
  const teacher = teacherFor(data, user, student);
  const editable = canEdit(user);

  function registerAttendance() {
    if (!editable || !student || !activity) return;
    const log = {
      id: generateId('log'),
      studentId: student.id,
      data: todayISO(),
      autorUid: user.uid,
      atividadeId: activity.id,
      objetivo: activity.objetivo,
      estrategia: activityToLogText(activity),
      observacao: 'Atividade aberta em HTML/PDF a partir do catálogo pedagógico.'
    };
    const next = { ...data, logs: [log, ...(data.logs || [])] };
    setData(next);
    onSave?.(next);
  }

  if (!activity) {
    return <section className='card'><h1>Atividade não encontrada</h1><p className='hint'>Volte ao catálogo e escolha uma atividade disponível.</p><Link className='action-link' to='/catalogo'>Voltar ao catálogo</Link></section>;
  }

  return <>
    <header className='header'>
      <div>
        <p>atividade pedagógica</p>
        <h1>{activity.titulo}</h1>
        <span>Versão HTML para consulta, impressão e geração de PDF individualizado.</span>
      </div>
    </header>

    <section className='card activity-sheet'>
      <div className='activity-meta-grid'>
        <div><b>Escola</b><span>{school?.nome || '-'}</span></div>
        <div><b>Professor</b><span>{teacher?.nome || '-'}</span></div>
        <div><b>Aluno</b><span>{student?.nome || 'Selecione um aluno'}</span></div>
        <div><b>Data</b><span>{formatDate(todayISO())}</span></div>
      </div>

      {students.length > 0 && <label className='student-picker'>Aluno para personalizar o PDF
        <select value={studentId} onChange={(event) => setStudentId(event.target.value)}>
          {students.map((item) => <option value={item.id} key={item.id}>{item.nome} — {item.serie}</option>)}
        </select>
      </label>}

      <ActivityPedagogicalCard activity={activity} />

      <div className='actions'>
        <button onClick={() => exportActivityPdf(activity, { data, student, user })}>Baixar PDF da atividade</button>
        <button onClick={() => window.print()}>Imprimir / salvar HTML</button>
        {editable && student && <button onClick={registerAttendance}>Registrar no atendimento</button>}
        {student && <Link className='action-link' to={`/aluno/${student.id}`}>Voltar à ficha do aluno</Link>}
      </div>
    </section>
  </>;
}
